function getProductHandle(pathname) {
  const match = String(pathname || '').match(/(?:^|\/)products\/([^/?#]+)/);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch (_error) {
    return match[1];
  }
}

function getVskProductData(parsedDocument) {
  const script = Array.from(parsedDocument.querySelectorAll('script'))
    .find((candidate) => candidate.textContent.includes('window.vskData'));
  if (!script) return null;

  const match = script.textContent.match(
    /["']product["']\s*:\s*(\{[\s\S]*?\})\s*,\s*["']page_type["']/
  );
  if (!match) return null;

  try {
    return JSON.parse(match[1]);
  } catch (_error) {
    return null;
  }
}

function getProductVariants(root) {
  const scripts = Array.from(root.querySelectorAll('script'));
  for (const script of scripts) {
    if (script.type !== 'application/json') continue;
    try {
      const data = JSON.parse(script.textContent);
      if (Array.isArray(data.variants)) return data.variants;
    } catch (_error) {
      // Ignore unrelated JSON blocks.
    }
  }
  return [];
}

function getVariantForSize(variants, size) {
  if (!Array.isArray(variants) || !size) return null;
  return variants.find((variant) => variant.option1 === size
    || variant.title === size
    || variant.options?.includes(size)) || null;
}

function resolveDestinationHandle(groups, currentHandle, optionValue) {
  if (!Array.isArray(groups) || !currentHandle || !optionValue) return null;

  const group = groups.find((candidate) => Array.isArray(candidate.option_values)
    && candidate.option_values.some((item) => item.handle === currentHandle));
  if (!group) return null;

  const destination = group.option_values.find((item) => item.published !== false
    && item.option_value === optionValue
    && item.handle !== currentHandle);

  return destination ? destination.handle : null;
}

function isEligibleColorClick(event, control) {
  if (!event || !control || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (control.matches('.swatch-selected')) return false;
  return !control.closest('.swatch-item-unavailable');
}

class ProductTransition {
  constructor(adapter) {
    this.adapter = adapter;
    this.active = false;
  }

  async navigate(destinationUrl, { pushHistory = true } = {}) {
    if (this.active) return false;

    this.active = true;
    const state = this.adapter.captureState();
    this.adapter.setBusy(true);

    try {
      const page = await this.adapter.fetchProduct(destinationUrl);
      this.adapter.replaceProduct(page);
      this.adapter.updateHistory(destinationUrl, pushHistory);

      if (!await this.adapter.waitForSwatches()) {
        throw new Error('Swatch King did not initialize');
      }

      this.adapter.restoreSize(state.size);
      this.adapter.restoreAnchor(state.anchorTop);
      this.adapter.initPaymentButtons();
      this.adapter.announce(page.title);
      this.adapter.dispatchChanged(destinationUrl);
      return true;
    } catch (_error) {
      this.adapter.hardNavigate(destinationUrl);
      return false;
    } finally {
      this.adapter.setBusy(false);
      this.active = false;
    }
  }
}

const PRODUCT_SECTION = '.shopify-section.section-main-product';
const PRODUCT_SWATCHES = 'variant-swatch-king .swatches-type-products';
const SELECTED_SIZE = 'variant-swatch-king .swatch-selector.swatch-selected[data-value]';

function createBrowserAdapter(win, doc) {
  const currentSection = () => doc.querySelector(PRODUCT_SECTION);

  return {
    captureState() {
      const swatches = doc.querySelector(PRODUCT_SWATCHES);
      const size = doc.querySelector(SELECTED_SIZE)?.dataset.value || null;
      return {
        size,
        anchorTop: swatches ? swatches.getBoundingClientRect().top : null
      };
    },

    setBusy(value) {
      const section = currentSection();
      if (section) section.toggleAttribute('aria-busy', value);
    },

    async fetchProduct(url) {
      const response = await win.fetch(url.href, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) {
        throw new Error(`Product request failed: ${response.status}`);
      }

      const parsed = new win.DOMParser().parseFromString(await response.text(), 'text/html');
      const section = parsed.querySelector(PRODUCT_SECTION);
      if (!section) throw new Error('Product section missing');

      return {
        section,
        title: parsed.title,
        canonical: parsed.querySelector('link[rel="canonical"]')?.href || url.href,
        vskProduct: getVskProductData(parsed),
        variants: getProductVariants(section)
      };
    },

    updateHistory(url, push) {
      if (push) {
        win.history.pushState({ binaProductAjax: true }, '', url.href);
      }
    },

    replaceProduct(page) {
      doc.title = page.title;
      const canonical = doc.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = page.canonical;
      if (page.vskProduct && win.vskData) win.vskData.product = page.vskProduct;

      const section = currentSection();
      const preservedSwatches = section.querySelector('variant-swatch-king');
      const destinationHandle = getProductHandle(
        new URL(page.canonical, win.location.origin).pathname
      );
      Array.from(section.attributes).forEach((attribute) => section.removeAttribute(attribute.name));
      Array.from(page.section.attributes)
        .forEach((attribute) => section.setAttribute(attribute.name, attribute.value));
      section.replaceChildren(...Array.from(page.section.childNodes));

      if (preservedSwatches) {
        preservedSwatches.dataset.binaPreserved = 'true';
        const picker = section.querySelector('variant-picker, .option-selectors, .selector-wrapper');
        if (picker) picker.before(preservedSwatches);

        preservedSwatches.querySelectorAll('.swatch-group-selector[swatch-url]')
          .forEach((control) => {
            const controlHandle = getProductHandle(control.getAttribute('swatch-url'));
            control.classList.toggle('swatch-selected', controlHandle === destinationHandle);
          });

        preservedSwatches.querySelectorAll('.swatch-selector[data-value]')
          .forEach((control) => {
            const variant = getVariantForSize(page.variants, control.dataset.value);
            control.closest('.swatch-view-item')
              ?.classList.toggle('swatch-item-unavailable', !variant?.available);
            control.classList.toggle(
              'swatch-selected',
              String(variant?.id) === String(page.vskProduct?.variant_id)
            );
          });
      }

      section.binaProductVariants = page.variants;
      section.vsk_initialized = true;
      section.setAttribute('swatch-generated', destinationHandle || 'true');
    },

    async waitForSwatches() {
      const deadline = Date.now() + 4000;
      while (Date.now() < deadline) {
        if (doc.querySelector(PRODUCT_SWATCHES)) return true;
        await new Promise((resolve) => win.setTimeout(resolve, 50));
      }
      return false;
    },

    restoreSize(size) {
      if (!size) return;
      const match = Array.from(doc.querySelectorAll('variant-swatch-king .swatch-selector[data-value]'))
        .find((control) => control.dataset.value === size);
      if (match
        && !match.closest('.swatch-item-unavailable')) match.click();
    },

    restoreAnchor(anchorTop) {
      if (anchorTop === null) return;
      const swatches = doc.querySelector(PRODUCT_SWATCHES);
      if (swatches) {
        win.scrollBy({
          top: swatches.getBoundingClientRect().top - anchorTop,
          behavior: 'auto'
        });
      }
    },

    initPaymentButtons() {
      if (win.Shopify?.PaymentButton?.init) win.Shopify.PaymentButton.init();
    },

    announce(title) {
      let status = doc.getElementById('bina-product-change-status');
      if (!status) {
        status = doc.createElement('div');
        status.id = 'bina-product-change-status';
        status.className = 'visually-hidden';
        status.setAttribute('aria-live', 'polite');
        doc.body.append(status);
      }
      status.textContent = title;
    },

    dispatchChanged(url) {
      doc.dispatchEvent(new win.CustomEvent('bina:product:changed', {
        detail: { url: url.href }
      }));
    },

    hardNavigate(url) {
      win.location.assign(url.href);
    }
  };
}

function installAjaxProductSwitcher(win, doc, dependencies = {}) {
  const transition = dependencies.transition
    || new ProductTransition(createBrowserAdapter(win, doc));

  const onClick = (event) => {
    const sizeControl = event.target?.closest?.(
      'variant-swatch-king[data-bina-preserved="true"] .swatch-selector[data-value]'
    );
    if (sizeControl) {
      if (sizeControl.closest('.swatch-item-unavailable')) return;
      const section = doc.querySelector(PRODUCT_SECTION);
      const variant = getVariantForSize(section?.binaProductVariants, sizeControl.dataset.value);
      const nativeInput = Array.from(doc.querySelectorAll(
        `${PRODUCT_SECTION} variant-picker input[type="radio"]`
      )).find((input) => input.value === sizeControl.dataset.value);
      if (!nativeInput || !variant?.available) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      sizeControl.closest('variant-swatch-king')
        .querySelectorAll('.swatch-selector.swatch-selected')
        .forEach((control) => control.classList.remove('swatch-selected'));
      sizeControl.classList.add('swatch-selected');
      nativeInput.click();

      section.querySelectorAll('[name="id"]')
        .forEach((input) => {
          input.value = String(variant.id);
          input.dispatchEvent(new win.Event('change', { bubbles: true }));
        });
      section.querySelectorAll('[name="add"], .add-to-cart')
        .forEach((button) => { button.disabled = false; });

      const variantUrl = new URL(win.location.href);
      variantUrl.searchParams.set('variant', variant.id);
      win.history.replaceState(win.history.state, '', variantUrl.href);
      return;
    }

    const control = event.target?.closest?.('.swatch-group-selector');
    if (!control || !isEligibleColorClick(event, control)) return;

    const currentHandle = getProductHandle(win.location.pathname);
    const destinationHandle = resolveDestinationHandle(
      win.starapps_data?.product_groups,
      currentHandle,
      control.dataset.value
    );
    if (!destinationHandle) return;

    const root = win.Shopify?.routes?.root || '/';
    const prefix = root.endsWith('/') ? root : `${root}/`;
    const destinationUrl = new URL(
      `${prefix}products/${encodeURIComponent(destinationHandle)}`,
      win.location.origin
    );

    event.preventDefault();
    event.stopImmediatePropagation();
    void transition.navigate(destinationUrl, { pushHistory: true });
  };

  const onPopState = () => {
    if (!getProductHandle(win.location.pathname)) return;
    void transition.navigate(new URL(win.location.href), { pushHistory: false });
  };

  doc.addEventListener('click', onClick, true);
  win.addEventListener('popstate', onPopState);

  return () => {
    doc.removeEventListener('click', onClick, true);
    win.removeEventListener('popstate', onPopState);
  };
}

globalThis.BinaAjaxProductSwitcher = {
  getProductHandle,
  getProductVariants,
  getVariantForSize,
  getVskProductData,
  resolveDestinationHandle,
  isEligibleColorClick,
  ProductTransition,
  createBrowserAdapter,
  installAjaxProductSwitcher
};

if (typeof window !== 'undefined'
  && typeof document !== 'undefined'
  && !window.__binaAjaxProductSwitcher) {
  window.__binaAjaxProductSwitcher = installAjaxProductSwitcher(window, document);
}
